// СПАРК ШАГА 3 — линия по карточкам, геометрия и четыре правила. B5-5.
//
// ИСТОЧНИК — `spark()` борда `Life Map - s3 Deck v8.html` (`:442–456`), плюс `mean` (`:422`) и
// `fullS` (`:424`). Спарк стоит во ВСЕХ ТРЁХ видах (`intro` `:469`, `deck` `:507`, `done` `:657`),
// и это байт борда, а не наша композиция: линия — общая шапка прохода, а не украшение деки.
//
// ⛔ ПОЧЕМУ ОТДЕЛЬНЫЙ МОДУЛЬ, А НЕ РАЗМЕТКА В ЭКРАНЕ. Здесь четыре ПРАВИЛА, и каждое — предмет,
// который можно сформулировать без браузера: точка это среднее ТОЛЬКО тронутых; маркер полый,
// пока карточка не собрана целиком; пропуск между точками рисуется пунктиром; римские
// прореживаются, когда их больше девяти. Спрятав их в JSX, мы получили бы четыре правила,
// проверяемых глазом по снимку.
//
// ⚠️ ТОЧКА — СРЕДНЕЕ ТРОНУТЫХ, А НЕ СРЕДНЕЕ СЕМИ, И ЭТО НЕ ОПТИМИЗАЦИЯ. Нетронутый слой это
// `null`, а не ноль (`s443.d5`); включи его в делитель — и карточка с одним сведённым слоем на
// `+3` показала бы `+0.43`, то есть почти середину. Человек двинул ползунок в край, а линия
// осталась у оси. Ровно эта же развилка ратифицирована в паспорте для силуэта §I (`s836.d1`:
// «среднее ТРОНУТЫХ слоёв»), и здесь она обязана читаться так же.
//
// ⛔ СВЯЗИ С ГЕЙТОМ НЕТ. Спарк рисует точку у КАЖДОЙ карточки, где тронут хоть один слой, — в том
// числе у той, что под полом (`nSliders < FLOOR`). Пол — правило перехода, а не правило рисунка;
// свести их значило бы объявить недобранную карточку несуществующей, хотя человек в ней работал.

import { LAYERS } from '../../contracts.js';
import { drawable } from './deckSlider.js';

/**
 * Зачин шапки спарка (борд `:450`).
 *
 * ⛔ ТА ЖЕ ТРИ СЛОВА СТОЯТ В КАПШНЕ ОТБИВКИ, И ЭТО ДВЕ РАЗНЫЕ СТРОКИ (`s516.d6`). Константа
 * живёт здесь, у шапки; отбивка собирает свою в `deckBeat.js`. Общий зачин — не повод на них
 * сэкономить: у шапки есть условный хвост `partial`, у капшна его нет вовсе.
 */
export const SPARK_HEAD_LEAD = 'the line so far';

/** Кадр спарка — байт борда (`:442`). Не «примерно 346×56»: числа сверяются с бордом тестом. */
export const SPARK_BOX = { W: 346, H: 56, PT: 7, PB: 12 };

/** Порог прореживания римских: при `n > 9` в подписи остаются первая, последняя и текущая. */
export const DENSE_AFTER = 9;

/** Заглушка прореженной подписи (борд `:456`). Точка, а не многоточие и не пустая строка. */
export const TICK_DIM = '·';

/**
 * Среднее ТРОНУТЫХ слоёв карточки. `null` — тронутых нет вовсе, точки у такой карточки не будет.
 *
 * ⭐ ФИЛЬТР — ТОТ ЖЕ `drawable`, ЧТО У ДОРОЖКИ (`B5-F3`, находка F-3 прогона `B5-QA`). Борд
 * фильтрует `num` (`:422`), и до правки это читалось как `Number.isFinite` — то есть линия
 * брала в среднее значение, которому на дорожке места нет: `deckSlider.drawable` требует ещё и
 * `∓3`, и `LayerRow` его чтит. Цена расхождения замерена: ряд из семи сотен давал точку на
 * `cy = −591` при кадре высотой 56 — десять кадров выше кромки, молча.
 *
 * ⛔ СЧЁТ ЭТИМ НЕ ЗАТРОНУТ. `nSliders`/`paneStat`/`cardFull` считают тронутость как `!== null`
 * и диапазон не смотрят (`s443.d5`) — значит карточка из семи значений вне ∓3 по-прежнему
 * «собрана», просто её точку рисовать негде. Это ДВА РАЗНЫХ ВОПРОСА, и так они и записаны в
 * `deckSlider.js:55–61`.
 *
 * Делитель — число тронутых, а не `LAYERS.length`. Обоснование в шапке файла; здесь стоит само
 * утверждение, чтобы «починка» на семь краснела тестом, а не доезжала до линии.
 */
export function meanTouched(row) {
  if (!Array.isArray(row)) return null;
  const v = row.filter(drawable);
  return v.length ? v.reduce((s, x) => s + x, 0) / v.length : null;
}

/** Собрана ли карточка целиком — все семь слоёв сведены (борд `fullS`). */
export function cardFull(row) {
  return Array.isArray(row) && row.length === LAYERS.length
    && row.every((v) => v !== null && v !== undefined);
}

/** Горизонталь i-й карточки в кадре (борд `:443`). Одна карточка — посередине, а не у края. */
export function tickX(i, n, box = SPARK_BOX) {
  return n === 1 ? box.W / 2 : 6 + i * ((box.W - 12) / (n - 1));
}

/** Вертикаль значения `v ∈ [-3, 3]` (борд `:443`). `+3` сверху, `−3` снизу. */
export function valueY(v, box = SPARK_BOX) {
  return box.PT + ((3 - v) / 6) * (box.H - box.PT - box.PB);
}

/**
 * Точки линии — по одной на карточку, где тронут хоть один слой.
 *
 * `part` — маркер ПОЛЫЙ: карточка начата, но не собрана. Индекс карточки сохраняется в `i`, и
 * именно по нему считается пропуск: пропущенная карточка не сдвигает соседей влево, она
 * оставляет в линии дырку.
 *
 * @param {Array<Array<number|null>>} vals ряды ползунков по этапам
 * @param {{W:number,H:number,PT:number,PB:number}} [box]
 * @returns {Array<{i:number,x:number,y:number,part:boolean}>}
 */
export function sparkPoints(vals, box = SPARK_BOX) {
  const rows = Array.isArray(vals) ? vals : [];
  const n = rows.length;
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const m = meanTouched(rows[i]);
    if (m === null) continue;
    out.push({ i, x: tickX(i, n, box), y: valueY(m, box), part: !cardFull(rows[i]) });
  }
  return out;
}

/**
 * Сегменты между соседними точками — кубическая кривая с горизонтальными ручками (борд `:445`).
 *
 * `gap` — между точками пропущена хотя бы одна карточка. Тогда сегмент рисуется пунктиром и
 * глуше: линия не утверждает, что между ними ничего не было, — она признаётся, что не знает.
 */
export function sparkSegments(points) {
  const segs = [];
  for (let k = 1; k < points.length; k += 1) {
    const q = points[k - 1];
    const p = points[k];
    const mx = (q.x + p.x) / 2;
    segs.push({
      d: `M${q.x} ${q.y} C${mx} ${q.y} ${mx} ${p.y} ${p.x} ${p.y}`,
      gap: p.i - q.i > 1,
    });
  }
  return segs;
}

/**
 * Шапка спарка (борд `:450`): сколько карточек СОБРАНО целиком и сколько начато, но не добрано.
 *
 * ⚠️ ХВОСТ ` · {K} partial` УСЛОВНЫЙ И СЧИТАЕТСЯ ПО ТОЧКАМ, А НЕ ПО КАРТОЧКАМ. `K` — это
 * начатые минус собранные; карточка, к которой не притрагивались, не начата и в хвост не идёт.
 * Хвоста нет вовсе, когда добирать нечего, — и это не «ноль partial», а отсутствие строки.
 *
 * ⛔ ЭТА СТРОКА И КАПШН ОТБИВКИ — РАЗНЫЕ, И СВОДИТЬ ИХ НЕЛЬЗЯ (`s516.d6`). Обе начинаются с
 * `the line so far`, но у капшна отбивки хвоста `partial` нет вовсе. Общий зачин — не повод
 * объявить их одной строкой: они говорят в разные моменты и про разное.
 */
export function sparkHead(vals) {
  const rows = Array.isArray(vals) ? vals : [];
  const total = rows.length;
  const done = rows.filter((row) => cardFull(row)).length;
  const started = sparkPoints(rows).length;
  const partial = Math.max(0, started - done);
  return { done, total, partial };
}

/**
 * Подписи римскими под линией (борд `:456`).
 *
 * ПРОРЕЖИВАНИЕ при `n > 9`: остаются первая, последняя и текущая, остальные — точка. Правило
 * борда дословно; причина видна на пятнадцати этапах — пятнадцать римских в 346px сливаются в
 * серую полосу, и подпись перестаёт быть подписью.
 *
 * ⚠️ `current` ПРИХОДИТ ПАРАМЕТРОМ, А НЕ ЧИТАЕТСЯ ИЗ АДРЕСА. У борда это `S.i` при виде `deck` и
 * `-1` во всех прочих: на интро и на финале текущей карточки нет. Разбор адреса — дело экрана.
 *
 * @param {string[]} romans римские по числу этапов (`src/roman.js`)
 * @param {number} current индекс текущей карточки, `-1` если её нет
 * @param {boolean[]} fullMask какие карточки собраны целиком
 */
export function sparkTicks(romans, current, fullMask = []) {
  const n = romans.length;
  const dense = n > DENSE_AFTER;
  return romans.map((roman, i) => ({
    label: (!dense || i === current || i === 0 || i === n - 1) ? roman : TICK_DIM,
    state: i === current ? 'cur' : (fullMask[i] ? 'on' : ''),
  }));
}

/**
 * Пульсирующий ореол у текущей НЕДОБРАННОЙ карточки (борд `:454`).
 *
 * `null`, когда карточки нет (интро, финал) или когда она собрана целиком: ореол — приглашение
 * доделать, и на готовой карточке он предлагал бы то, что уже сделано. Нетронутая карточка тоже
 * получает ореол, и он садится на ось (`mean ?? 0` борда) — точки у неё нет, а место есть.
 */
export function sparkHalo(vals, current, box = SPARK_BOX) {
  const rows = Array.isArray(vals) ? vals : [];
  if (current < 0 || current >= rows.length) return null;
  if (cardFull(rows[current])) return null;
  return { x: tickX(current, rows.length, box), y: valueY(meanTouched(rows[current]) ?? 0, box) };
}
